import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, Star, Calendar, BookOpen, Award, MessageSquare, DollarSign } from "lucide-react";

/**
 * Public mentor profile — expertise, publications, rating, availability,
 * hourly rate. Anyone (auth or not) can view. From this page a signed-in
 * client can jump straight to /dashboard/guidance/new with the mentor
 * pre-selected.
 */
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function MentorPublicProfile() {
  const { mentorId } = useParams();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get(`/mentors/${mentorId}`).then((r) => setM(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Not found"));
  }, [mentorId]);

  if (err) return <div className="p-10 text-sm text-rose-600">Mentor not found: {err}</div>;
  if (!m) return <div className="p-10 kb-mono text-xs text-kb-ink-muted">Loading…</div>;

  const windows = m.availability_windows || [];
  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="mentor-public-profile">
      <button onClick={() => navigate(-1)} className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted hover:text-kb-ink inline-flex items-center gap-1">
        <ArrowLeft className="w-3 h-3" /> Back
      </button>

      <div className="mt-6 flex flex-wrap items-start gap-6">
        {m.photo_url ? <img src={m.photo_url} alt="" className="w-28 h-28 rounded-sm object-cover border border-slate-200" /> :
          <div className="w-28 h-28 rounded-sm bg-kb-primary/10 flex items-center justify-center kb-mono text-3xl text-kb-primary">{m.name?.[0]?.toUpperCase()}</div>}
        <div className="flex-1 min-w-0">
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">MENTOR</div>
          <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">{m.title ? `${m.title} ` : ""}{m.name}</h1>
          <div className="text-sm text-kb-ink-muted mt-1">{m.affiliation}</div>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
            {m.hourly_rate_npr != null ? (
              <span className="inline-flex items-center gap-1 text-emerald-700 font-medium"><DollarSign className="w-4 h-4" /> NPR {Number(m.hourly_rate_npr).toLocaleString()}/hr</span>
            ) : <span className="kb-mono text-[10px] text-amber-700">Rate TBD — admin will price manually</span>}
            {typeof m.avg_rating === "number" && (
              <span className="inline-flex items-center gap-1 text-amber-600"><Star className="w-4 h-4 fill-amber-500" /> {m.avg_rating} <span className="text-kb-ink-muted">({m.ratings_count || 0})</span></span>
            )}
            {typeof m.sessions_completed === "number" && m.sessions_completed > 0 && (
              <span className="inline-flex items-center gap-1 text-kb-ink-muted"><MessageSquare className="w-4 h-4" /> {m.sessions_completed} sessions</span>
            )}
            {typeof m.h_index === "number" && m.h_index > 0 && (
              <span className="inline-flex items-center gap-1 text-kb-ink-muted"><Award className="w-4 h-4" /> h-index {m.h_index}</span>
            )}
          </div>
        </div>
        <Link to={`/dashboard/guidance/new?mentor_id=${m.id}`} className="kb-btn-primary text-sm" data-testid="mentor-public-request-btn">
          Request guidance
        </Link>
      </div>

      {m.bio && (
        <section className="mt-8 kb-card p-5">
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mb-2">ABOUT</div>
          <p className="text-sm text-kb-ink leading-relaxed whitespace-pre-wrap">{m.bio}</p>
        </section>
      )}

      {(m.expertise || []).length > 0 && (
        <section className="mt-6 kb-card p-5">
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mb-2">EXPERTISE</div>
          <div className="flex flex-wrap gap-1.5">
            {(m.expertise || []).map((e, i) => (
              <span key={i} className="kb-mono text-[11px] px-2 py-1 rounded-sm border border-slate-200 bg-slate-50 text-kb-ink">{e}</span>
            ))}
          </div>
          {(m.domains || []).length > 0 && (
            <div className="mt-3 kb-mono text-[10px] text-kb-ink-muted">Domains · {(m.domains || []).join(" · ")}</div>
          )}
        </section>
      )}

      <section className="mt-6 kb-card p-5">
        <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mb-2 inline-flex items-center gap-1">
          <Calendar className="w-3 h-3" /> AVAILABILITY
        </div>
        {windows.length === 0 ? (
          <div className="text-sm text-kb-ink-muted">Mentor hasn&apos;t published availability windows yet — propose any slot and they&apos;ll respond.</div>
        ) : (
          <ul className="text-sm text-kb-ink space-y-1">
            {windows.map((w, i) => (
              <li key={i} className="inline-flex items-center gap-2 mr-4">
                <span className="kb-mono text-[10px] px-1.5 py-0.5 rounded-sm bg-kb-primary text-white">{DAY_LABELS[w.day] || `Day ${w.day}`}</span>
                <span>{w.start} – {w.end}</span>
                <span className="kb-mono text-[9px] text-kb-ink-light">({w.tz || "Asia/Kathmandu"})</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {typeof m.publications_count === "number" && m.publications_count > 0 && (
        <section className="mt-6 kb-card p-5">
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mb-2 inline-flex items-center gap-1">
            <BookOpen className="w-3 h-3" /> PUBLICATIONS
          </div>
          <div className="text-sm text-kb-ink">{m.publications_count} indexed publication{m.publications_count === 1 ? "" : "s"}</div>
        </section>
      )}
    </div>
  );
}
